import React, { useEffect, useState } from 'react';
import api from '../api';
import { Shield, Plus, Download, Upload, RefreshCw, Copy, ExternalLink } from 'lucide-react';

export default function RpkiCA() {
  const [cas, setCas] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [selectedCa, setSelectedCa] = useState(null);

  const load = async () => {
    setLoading(true); setErr('');
    try {
      const r = await api.get('/krill/cas');
      setCas(r.data?.cas || r.data || []);
    } catch (e) { setErr(e.response?.data?.error || 'Krill inacessível'); setCas([]); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  return (
    <>
      <div className="page-title">
        <h1><Shield size={20} style={{verticalAlign:'middle'}}/> RPKI CA · Delegated</h1>
        <button onClick={load} className="ghost" disabled={loading}>
          <RefreshCw size={14}/> {loading ? 'Atualizando…' : 'Atualizar'}
        </button>
      </div>

      <div style={{padding:'1rem', background:'#0f172a', border:'1px solid #1e3a8a', borderRadius:'8px', marginBottom:'1.5rem', fontSize:'.88rem'}}>
        <strong style={{color:'#3b82f6'}}>O que é isso?</strong>
        <p style={{margin:'.4rem 0 0', color:'#a1a1aa'}}>
          O <strong>Krill</strong> é a CA (Certificate Authority) local que gerencia suas ROAs no modo
          <strong> RPKI Delegado</strong> do Registro.br. Aqui você gera o <strong>Child Request XML</strong> que
          submete em <a href="https://registro.br/tecnologia/numeracao/rpki/" target="_blank" rel="noreferrer" style={{color:'#3b82f6'}}>registro.br/tecnologia/numeracao/rpki</a>.
        </p>
        <p style={{margin:'.4rem 0 0', color:'#a1a1aa'}}>
          Fluxo: criar CA → baixar child_request.xml → submeter no Registro.br → receber parent_response.xml → fazer upload aqui → criar ROAs.
        </p>
      </div>

      {err && (
        <div className="err-msg" style={{padding:'.6rem 1rem', background:'rgba(239,68,68,.1)', border:'1px solid #ef4444', borderRadius:'6px'}}>
          {err}
        </div>
      )}

      {/* Lista de CAs */}
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
        <h3 style={{margin:0}}>CAs configuradas</h3>
        <button onClick={()=>setShowCreate(true)}><Plus size={14}/> Criar CA</button>
      </div>

      {cas && cas.length === 0 ? (
        <div style={{padding:'1rem', background:'rgba(245,158,11,.1)', border:'1px solid #f59e0b', borderRadius:'8px', marginTop:'1rem', fontSize:'.88rem'}}>
          <strong style={{color:'#f59e0b'}}>Nenhuma CA criada</strong>
          <p style={{margin:'.4rem 0 0', color:'#a1a1aa'}}>
            Clique em "Criar CA" para iniciar. Recomendamos usar o handle <code>hiperlink</code> (ou nome curto que identifica sua empresa).
          </p>
        </div>
      ) : cas && (
        <table style={{marginTop:'1rem'}}>
          <thead><tr><th>Handle</th><th>Pais (parents)</th><th>Ações</th></tr></thead>
          <tbody>
            {cas.map(h => (
              <tr key={h}>
                <td><strong style={{fontFamily:'JetBrains Mono', color:'#3b82f6'}}>{h}</strong></td>
                <td><span style={{color:'#a1a1aa'}}>—</span></td>
                <td><button onClick={()=>setSelectedCa(h)} className="secondary">Gerenciar</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showCreate && <CreateCaModal onClose={()=>{ setShowCreate(false); load(); }} />}
      {selectedCa && <CaManageModal handle={selectedCa} onClose={()=>{ setSelectedCa(null); load(); }} />}
    </>
  );
}

function CreateCaModal({ onClose }) {
  const [handle, setHandle] = useState('hiperlink');
  const [msg, setMsg] = useState('');

  const create = async () => {
    setMsg('Criando CA…');
    try {
      await api.post('/krill/cas', { handle });
      onClose();
    } catch (e) { setMsg(e.response?.data?.error?.toString() || e.response?.data?.error || 'Erro'); }
  };

  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={e=>e.stopPropagation()}>
        <h2>Criar CA RPKI</h2>
        <label>Handle (identificador único)</label>
        <input value={handle} onChange={e=>setHandle(e.target.value)}
          placeholder="hiperlink" style={{fontFamily:'JetBrains Mono'}} />
        <p style={{color:'#a1a1aa', fontSize:'.78rem', marginTop:'.3rem'}}>
          Use um nome curto sem espaços (ex: "hiperlink", "as270776"). Esse handle aparecerá no XML
          que você envia ao Registro.br.
        </p>
        {msg && <div className="err-msg">{msg}</div>}
        <div className="modal-actions">
          <button className="secondary" onClick={onClose}>Cancelar</button>
          <button onClick={create} disabled={!handle}>Criar CA</button>
        </div>
      </div>
    </div>
  );
}

function CaManageModal({ handle, onClose }) {
  const [tab, setTab] = useState('parent');
  const [childRequest, setChildRequest] = useState(null);
  const [pubRequest, setPubRequest] = useState(null);
  const [parentXml, setParentXml] = useState('');
  const [parentHandle, setParentHandle] = useState('registro-br');
  const [msg, setMsg] = useState('');
  const [routes, setRoutes] = useState([]);
  const [newRoute, setNewRoute] = useState({ asn: '', prefix: '', max_length: '' });

  const loadAll = async () => {
    try {
      const [cr, pr, ro] = await Promise.allSettled([
        api.get(`/krill/cas/${handle}/child_request`, { responseType: 'text', transformResponse: r => r }),
        api.get(`/krill/cas/${handle}/publisher_request`, { responseType: 'text', transformResponse: r => r }),
        api.get(`/krill/cas/${handle}/routes`),
      ]);
      if (cr.status === 'fulfilled') setChildRequest(cr.value.data);
      if (pr.status === 'fulfilled') setPubRequest(pr.value.data);
      if (ro.status === 'fulfilled') setRoutes(ro.value.data || []);
    } catch {}
  };
  useEffect(() => { loadAll(); }, [handle]);

  const download = (filename, content) => {
    const blob = new Blob([content], { type: 'application/xml' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
  };

  const submitParent = async () => {
    setMsg('Enviando parent response…');
    try {
      await api.post(`/krill/cas/${handle}/parent`, { parentHandle, response: parentXml });
      setMsg('✓ Parent registrado — Krill já pode emitir ROAs sob este pai');
      loadAll();
    } catch (e) { setMsg(e.response?.data?.error?.toString() || 'Erro'); }
  };

  const addRoute = async () => {
    setMsg('Adicionando ROA…');
    try {
      const cidr = newRoute.prefix.split('/');
      await api.post(`/krill/cas/${handle}/routes`, {
        added: [{
          asn: parseInt(newRoute.asn.replace(/^AS/i, ''), 10),
          prefix: newRoute.prefix,
          max_length: parseInt(newRoute.max_length || cidr[1], 10),
        }]
      });
      setMsg('✓ ROA criada');
      setNewRoute({ asn: '', prefix: '', max_length: '' });
      loadAll();
    } catch (e) { setMsg(e.response?.data?.error?.toString() || 'Erro'); }
  };

  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={e=>e.stopPropagation()} style={{minWidth:'700px', maxWidth:'90vw'}}>
        <h2>CA · {handle}</h2>

        <div className="tab-bar" style={{marginTop:'.5rem'}}>
          <div className={'tab ' + (tab==='parent'?'active':'')} onClick={()=>setTab('parent')}>1. Child Request</div>
          <div className={'tab ' + (tab==='response'?'active':'')} onClick={()=>setTab('response')}>2. Parent Response</div>
          <div className={'tab ' + (tab==='routes'?'active':'')} onClick={()=>setTab('routes')}>3. ROAs</div>
        </div>

        {tab === 'parent' && (
          <>
            <p style={{color:'#a1a1aa', fontSize:'.85rem'}}>
              Baixe este XML e submeta no Registro.br. Você receberá um XML de resposta (parent_response.xml).
            </p>
            <button onClick={()=>download(`child_request_${handle}.xml`, childRequest || '')} disabled={!childRequest}>
              <Download size={12}/> Baixar child_request.xml
            </button>
            {' '}<button onClick={()=>{navigator.clipboard.writeText(childRequest||''); setMsg('Copiado'); }} disabled={!childRequest} className="secondary">
              <Copy size={12}/> Copiar
            </button>

            <h4 style={{marginTop:'1rem'}}>Conteúdo do Child Request</h4>
            <pre className="code" style={{maxHeight:'350px', overflow:'auto', fontSize:'.7rem'}}>{childRequest || '(carregando…)'}</pre>

            <p style={{marginTop:'.6rem', color:'#a1a1aa', fontSize:'.82rem'}}>
              📍 Submeter em: <a href="https://registro.br/tecnologia/numeracao/rpki/" target="_blank" rel="noreferrer" style={{color:'#3b82f6'}}>
              registro.br/tecnologia/numeracao/rpki/ <ExternalLink size={10}/></a>
            </p>
          </>
        )}

        {tab === 'response' && (
          <>
            <p style={{color:'#a1a1aa', fontSize:'.85rem'}}>
              Cole aqui o XML <strong>parent_response.xml</strong> que o Registro.br te enviou:
            </p>
            <label>Parent Handle (nome do pai — use "registro-br" se em dúvida)</label>
            <input value={parentHandle} onChange={e=>setParentHandle(e.target.value)} placeholder="registro-br" />
            <label>Parent Response XML</label>
            <textarea value={parentXml} onChange={e=>setParentXml(e.target.value)}
              placeholder="<parent_response ...>" style={{minHeight:'200px', fontFamily:'JetBrains Mono', fontSize:'.78rem'}} />
            <div style={{marginTop:'.6rem'}}>
              <button onClick={submitParent} disabled={!parentXml || !parentHandle}>
                <Upload size={12}/> Enviar parent response
              </button>
            </div>
          </>
        )}

        {tab === 'routes' && (
          <>
            <p style={{color:'#a1a1aa', fontSize:'.85rem'}}>
              Após registrar o parent, crie ROAs para autorizar seus ASNs a anunciar prefixos.
            </p>
            <h4>ROAs ativas</h4>
            <table>
              <thead><tr><th>ASN</th><th>Prefixo</th><th>Max Length</th></tr></thead>
              <tbody>
                {routes.length === 0 ? <tr><td colSpan={3} style={{color:'#a1a1aa'}}>Sem ROAs ainda.</td></tr> :
                  routes.map((r,i)=>(
                    <tr key={i}>
                      <td style={{fontFamily:'JetBrains Mono'}}>AS{r.asn}</td>
                      <td style={{fontFamily:'JetBrains Mono'}}>{r.prefix}</td>
                      <td>{r.max_length}</td>
                    </tr>
                  ))
                }
              </tbody>
            </table>

            <h4 style={{marginTop:'1rem'}}>Nova ROA</h4>
            <div className="row">
              <input value={newRoute.asn} onChange={e=>setNewRoute({...newRoute, asn: e.target.value})} placeholder="AS65000" />
              <input value={newRoute.prefix} onChange={e=>setNewRoute({...newRoute, prefix: e.target.value})} placeholder="203.0.113.0/24" />
              <input value={newRoute.max_length} onChange={e=>setNewRoute({...newRoute, max_length: e.target.value})} placeholder="24" type="number" />
              <button onClick={addRoute} disabled={!newRoute.asn || !newRoute.prefix}>+ ROA</button>
            </div>
          </>
        )}

        {msg && <div style={{marginTop:'.8rem', color: msg.startsWith('✓') ? '#10b981' : msg.startsWith('Erro') ? '#ef4444' : '#a1a1aa'}}>{msg}</div>}

        <div className="modal-actions">
          <button onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  );
}
